// apps/web/src/components/TemplateGalleryModal.js
import React, { useEffect, useState } from 'react';
import { useToast } from './Toast';
import TemplateDeploymentModal from './TemplateDeploymentModal';

/**
 * TemplateGalleryModal
 * - Shows user's LIBRARY (owned templates only)
 * - Fetches from /api/v1/templates/library
 * - Lets the user pick one to apply to a list
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - listId: string - The list to apply templates to
 *  - onTasksCreated: (tasks) => void - Called after template successfully applied
 *
 * Note: For browsing/purchasing templates, use the Marketplace (/marketplace)
 */
export default function TemplateGalleryModal({ open = false, onClose, listId, onTasksCreated }) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // Load the list when the modal opens
  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      setTemplates([]);
      setSelectedId(null);
      setSelectedTemplate(null);

      try {
        // Fetch user's library (owned templates only)
        const token = localStorage.getItem('accessToken');
        if (!token) {
          throw new Error('Please log in to view your template library');
        }

        const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1'}/templates/library`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          const txt = await res.text().catch(() => '');
          throw new Error(txt || `HTTP ${res.status}`);
        }

        const data = await res.json();

        if (!cancelled) {
          const list = Array.isArray(data?.templates) ? data.templates : [];
          setTemplates(list);
          // Preselect first item - the other useEffect will fetch details
          if (list.length > 0) {
            setSelectedId(list[0].id);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'Failed to load templates');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // When the user changes selection, fetch the full template (detail endpoint)
  useEffect(() => {
    if (!open || !selectedId) return;
    fetchTemplateById(selectedId, setSelectedTemplate, setError);
  }, [open, selectedId]);

  function handleUse() {
    if (!selectedTemplate) {
      toast.error('No template selected');
      return;
    }
    // Show deployment modal
    setShowDeployModal(true);
  }

  function handleDeploymentComplete(tasks) {
    // Close both modals
    setShowDeployModal(false);
    onClose();
    // Notify parent
    if (onTasksCreated) {
      onTasksCreated(tasks);
    }
  }

  if (!open) return null;

  return (
    <div
      className="ModalOverlay"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="TaskEditPanel" role="dialog" aria-modal="true" aria-label="Template Gallery">
        {/* Header */}
        <div className="TaskEditHeader">
          <div className="TaskEditHeaderLeft">
            <div className="TaskEditHeading">{showHelp ? 'Template Help' : 'My Template Library'}</div>
            <div className="TaskEditSub">{showHelp ? 'How to use templates' : 'Pick a template to apply to this list.'}</div>
          </div>
          <div className="TaskEditHeaderRight" style={{ display: 'flex', gap: 8 }}>
            <button
              className="Btn"
              onClick={() => setShowHelp(!showHelp)}
              style={{ backgroundColor: showHelp ? '#3b82f6' : '#f1f5f9', color: showHelp ? 'white' : '#1e293b' }}
            >
              {showHelp ? '📋 Library' : '❓ Help'}
            </button>
            <button className="Btn" onClick={onClose}>Close</button>
          </div>
        </div>

        {/* Body */}
        {showHelp ? (
          <UserHelpView />
        ) : loading ? (
          <div className="RightListEmpty">Loading templates…</div>
        ) : error ? (
          <div className="RightHint">Error: {error}</div>
        ) : templates.length === 0 ? (
          <div className="RightListEmpty">
            <p>You don't have any templates yet.</p>
            <p style={{ marginTop: 8, fontSize: 14 }}>
              Visit the <strong>Marketplace</strong> to browse and get templates!
            </p>
          </div>
        ) : (
          <>
            {/* Simple card list using existing styles */}
            <ul className="RightList" role="listbox" style={{ maxHeight: 360, overflowY: 'auto' }}>
              {templates.map((t) => {
                const isSelected = selectedId === t.id;
                return (
                  <li
                    key={t.id}
                    role="option"
                    className="RightListItem"
                    onClick={() => setSelectedId(t.id)}
                    style={{
                      cursor: 'pointer',
                      border: isSelected ? '1px solid #10b981' : undefined,
                      outline: isSelected ? '2px solid rgba(16,185,129,0.25)' : 'none',
                    }}
                    aria-selected={isSelected ? 'true' : 'false'}
                  >
                    <div className="CollabName" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{t.title || t.id}</span>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        {/* Category tag */}
                        {t.category ? (
                          <span className="CollabTag">{t.category}</span>
                        ) : null}
                      </div>
                    </div>
                    {t.summary ? (
                      <div className="CollabDetails" style={{ marginTop: 4 }}>
                        {t.summary}
                      </div>
                    ) : null}
                    {/* Tags row */}
                    {Array.isArray(t.tags) && t.tags.length > 0 ? (
                      <div className="CollabTagRow">
                        {t.tags.map((tag, i) => (
                          <span key={i} className="CollabTag">{String(tag)}</span>
                        ))}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>

            {/* Preview (optional) */}
            <div className="TaskEditMeta" style={{ marginTop: 8 }}>
              <div className="TaskEditMetaLabel">Preview</div>
              <div className="TaskEditMetaValue" style={{ marginTop: 6 }}>
                {!selectedTemplate ? (
                  <div className="RightListEmpty">(select a template to preview)</div>
                ) : (
                  <>
                    <div style={{ fontWeight: 600 }}>{selectedTemplate.title || selectedTemplate.id}</div>
                    {selectedTemplate.summary ? (
                      <div style={{ color: '#475569', marginTop: 4 }}>{selectedTemplate.summary}</div>
                    ) : null}
                    {/* Render the template HTML */}
                    {selectedTemplate.html ? (
                      <div
                        style={{
                          marginTop: 8,
                          background: '#f1f5f9',
                          padding: 8,
                          borderRadius: 6,
                          border: '1px solid #cbd5e1',
                          fontSize: 14,
                          maxHeight: 400,
                          overflowY: 'auto',
                        }}
                        dangerouslySetInnerHTML={{ __html: selectedTemplate.html }}
                      />
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </>
        )}

        {/* Actions */}
        <div className="TaskEditActions" style={{ marginTop: 12, display: 'flex', gap: 8 }}>
          <button
            className="Btn primary"
            disabled={!selectedTemplate}
            onClick={handleUse}
          >
            Use this template
          </button>
        </div>
      </div>

      {/* Deployment Modal - shows when user clicks "Use this template" */}
      {showDeployModal && selectedTemplate && (
        <TemplateDeploymentModal
          template={selectedTemplate}
          listId={listId}
          onClose={() => setShowDeployModal(false)}
          onTasksCreated={handleDeploymentComplete}
        />
      )}
    </div>
  );
}

/** Helper: fetch a single template by id */
async function fetchTemplateById(id, setSelectedTemplate, setError) {
  try {
    setError?.('');
    const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1'}/templates/${encodeURIComponent(id)}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(localStorage.getItem('accessToken')
          ? { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
          : {}),
      },
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(txt || `HTTP ${res.status}`);
    }
    const data = await res.json();
    // API returns { template: {...} }
    setSelectedTemplate?.(data?.template || null);
  } catch (err) {
    setError?.(err?.message || 'Failed to load template details');
    setSelectedTemplate?.(null);
  }
}

/**
 * UserHelpView - Complete user guide for templates
 */
function UserHelpView() {
  return (
    <div style={{ padding: 24, maxWidth: 900, margin: '0 auto', maxHeight: 600, overflowY: 'auto' }}>
      <h1 style={{ fontSize: 28, marginBottom: 8 }}>User Guide: Template Marketplace</h1>
      <p style={{ color: '#64748b', marginBottom: 32 }}>Last Updated: 2025-01-07</p>

      {/* Getting Started */}
      <section style={{ marginBottom: 40 }}>
        <h2 style={{ fontSize: 22, marginBottom: 16, borderBottom: '2px solid #e2e8f0', paddingBottom: 8 }}>
          🚀 Getting Started
        </h2>

        <h3 style={{ fontSize: 18, marginTop: 20, marginBottom: 8 }}>What is the Template Marketplace?</h3>
        <p style={{ lineHeight: 1.6, color: '#475569', marginBottom: 16 }}>
          The Template Marketplace is a collection of pre-built task templates designed to help you get started quickly with common workflows and projects.
        </p>

        <div style={{ backgroundColor: '#f8fafc', padding: 16, borderRadius: 8, marginBottom: 16 }}>
          <strong style={{ display: 'block', marginBottom: 8 }}>Benefits:</strong>
          <ul style={{ lineHeight: 1.8, color: '#475569', paddingLeft: 20, margin: 0 }}>
            <li>🚀 Save time with ready-made task lists</li>
            <li>✨ Professional workflows designed by experts</li>
            <li>🎯 Customizable to your specific needs</li>
            <li>📚 Growing library of templates</li>
          </ul>
        </div>

        <h3 style={{ fontSize: 18, marginTop: 20, marginBottom: 8 }}>Template Types</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ padding: 16, backgroundColor: '#f0fdf4', borderRadius: 8, border: '1px solid #86efac' }}>
            <h4 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#166534' }}>Free Templates</h4>
            <ul style={{ fontSize: 14, lineHeight: 1.6, color: '#475569', paddingLeft: 20, margin: 0 }}>
              <li>Available to all users immediately</li>
              <li>No purchase required</li>
              <li>Basic workflows and checklists</li>
              <li>Great for trying out the system</li>
            </ul>
          </div>
          <div style={{ padding: 16, backgroundColor: '#fef3c7', borderRadius: 8, border: '1px solid #fde047' }}>
            <h4 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#854d0e' }}>Premium Templates</h4>
            <ul style={{ fontSize: 14, lineHeight: 1.6, color: '#475569', paddingLeft: 20, margin: 0 }}>
              <li>One-time purchase (£7.99 - £49.99)</li>
              <li>Advanced workflows with interactive variables</li>
              <li>Specialized for specific industries/use cases</li>
              <li>Purchase once, use unlimited times</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Browsing Templates */}
      <section style={{ marginBottom: 40 }}>
        <h2 style={{ fontSize: 22, marginBottom: 16, borderBottom: '2px solid #e2e8f0', paddingBottom: 8 }}>
          📋 Browsing Templates
        </h2>

        <h3 style={{ fontSize: 18, marginTop: 20, marginBottom: 8 }}>Template Categories</h3>
        <p style={{ lineHeight: 1.6, color: '#475569', marginBottom: 12 }}>Templates are organised by category:</p>
        <ul style={{ lineHeight: 1.8, color: '#475569', paddingLeft: 20 }}>
          <li><strong>General:</strong> Universal templates for common tasks</li>
          <li><strong>Academic:</strong> Student projects, assignments, research</li>
          <li><strong>Work:</strong> Business processes, project management</li>
          <li><strong>Personal:</strong> Life organisation, goals, habits</li>
          <li><strong>Marketing:</strong> Campaigns, launches, content planning</li>
          <li><strong>Development:</strong> Software projects, sprints, releases</li>
          <li><strong>Design:</strong> Creative workflows, design processes</li>
        </ul>

        <h3 style={{ fontSize: 18, marginTop: 20, marginBottom: 8 }}>Finding Templates</h3>
        <div style={{ backgroundColor: '#f1f5f9', padding: 16, borderRadius: 8 }}>
          <p style={{ margin: 0, marginBottom: 12, color: '#475569' }}>
            <strong>Filter by Pricing:</strong> Use the Free/Premium/All tabs to narrow your search
          </p>
          <p style={{ margin: 0, marginBottom: 12, color: '#475569' }}>
            <strong>Browse by Category:</strong> Scroll through organized categories
          </p>
          <p style={{ margin: 0, color: '#475569' }}>
            <strong>Featured Templates:</strong> Top-quality templates highlighted at the top
          </p>
        </div>
      </section>

      {/* Deploying Templates */}
      <section style={{ marginBottom: 40 }}>
        <h2 style={{ fontSize: 22, marginBottom: 16, borderBottom: '2px solid #e2e8f0', paddingBottom: 8 }}>
          🎯 Deploying Templates
        </h2>

        <h3 style={{ fontSize: 18, marginTop: 20, marginBottom: 8 }}>How to Deploy</h3>
        <ol style={{ lineHeight: 1.8, color: '#475569', paddingLeft: 20 }}>
          <li>Open Template Gallery (📋 Templates button)</li>
          <li>Select a template (free or purchased)</li>
          <li>Click "Apply Template" button</li>
          <li>Fill in Variables (if template is interactive)</li>
          <li>Click "Deploy" button</li>
          <li>Tasks created in your current list!</li>
        </ol>

        <h3 style={{ fontSize: 18, marginTop: 20, marginBottom: 8 }}>Interactive Templates</h3>
        <p style={{ lineHeight: 1.6, color: '#475569', marginBottom: 12 }}>
          Some templates have <strong>variables</strong> that let you customize them before deployment.
        </p>

        <div style={{ backgroundColor: '#eff6ff', padding: 16, borderRadius: 8, marginBottom: 16, border: '1px solid #bfdbfe' }}>
          <strong style={{ color: '#1e40af' }}>Example: "Product Launch Checklist"</strong>
          <ul style={{ lineHeight: 1.6, color: '#475569', paddingLeft: 20, marginTop: 8, marginBottom: 0 }}>
            <li>Variable: Product Name → "My Awesome App"</li>
            <li>Variable: Launch Date → "2025-02-14"</li>
            <li>Variable: Team Size → 5</li>
          </ul>
        </div>

        <h3 style={{ fontSize: 18, marginTop: 20, marginBottom: 8 }}>Variable Types</h3>
        <ul style={{ lineHeight: 1.8, color: '#475569', paddingLeft: 20 }}>
          <li><strong>Text Input:</strong> Enter free text (names, descriptions, etc.)</li>
          <li><strong>Number Input:</strong> Enter numeric values (team size, budget, duration)</li>
          <li><strong>Date Input:</strong> Select dates from calendar picker</li>
          <li><strong>Dropdown:</strong> Choose one option from a list (Priority, Status)</li>
          <li><strong>Checkbox:</strong> Yes/No toggle (Include testing phase?)</li>
          <li><strong>Checkbox Group:</strong> Select multiple options (Platforms, Features)</li>
        </ul>

        <h3 style={{ fontSize: 18, marginTop: 20, marginBottom: 8 }}>Static Templates</h3>
        <p style={{ lineHeight: 1.6, color: '#475569' }}>
          Some templates don't have variables - they create a fixed set of tasks. No customization needed, quick one-click deployment.
        </p>
      </section>

      {/* FAQ */}
      <section style={{ marginBottom: 40 }}>
        <h2 style={{ fontSize: 22, marginBottom: 16, borderBottom: '2px solid #e2e8f0', paddingBottom: 8 }}>
          ❓ Frequently Asked Questions
        </h2>

        <div style={{ marginBottom: 16 }}>
          <h4 style={{ fontSize: 16, marginBottom: 8, color: '#1e293b' }}>How many times can I use a template after purchase?</h4>
          <p style={{ margin: 0, color: '#475569', lineHeight: 1.6 }}>
            <strong>Unlimited!</strong> Purchase once, deploy as many times as you want.
          </p>
        </div>

        <div style={{ marginBottom: 16 }}>
          <h4 style={{ fontSize: 16, marginBottom: 8, color: '#1e293b' }}>Can I share purchased templates with teammates?</h4>
          <p style={{ margin: 0, color: '#475569', lineHeight: 1.6 }}>
            Currently, purchases are per-user. If you deploy a template to a shared workspace, others see the tasks,
            but they can't deploy the template themselves unless they also purchase it.
          </p>
        </div>

        <div style={{ marginBottom: 16 }}>
          <h4 style={{ fontSize: 16, marginBottom: 8, color: '#1e293b' }}>What if a template doesn't fit my exact needs?</h4>
          <p style={{ margin: 0, color: '#475569', lineHeight: 1.6 }}>
            Many templates have variables that let you customize them. After deployment, you can also edit the
            generated tasks manually to fine-tune them.
          </p>
        </div>

        <div style={{ marginBottom: 16 }}>
          <h4 style={{ fontSize: 16, marginBottom: 8, color: '#1e293b' }}>Can I undo a template deployment?</h4>
          <p style={{ margin: 0, color: '#475569', lineHeight: 1.6 }}>
            Currently, no automatic undo. You'll need to manually delete the created tasks.
            Future versions may include bulk delete or undo functionality.
          </p>
        </div>
      </section>

      {/* Tips & Tricks */}
      <section style={{ marginBottom: 40 }}>
        <h2 style={{ fontSize: 22, marginBottom: 16, borderBottom: '2px solid #e2e8f0', paddingBottom: 8 }}>
          💡 Tips & Tricks
        </h2>

        <h3 style={{ fontSize: 18, marginTop: 20, marginBottom: 8 }}>Getting the Most from Templates</h3>
        <ul style={{ lineHeight: 1.8, color: '#475569', paddingLeft: 20 }}>
          <li><strong>Start with Free:</strong> Try free templates first to understand how they work</li>
          <li><strong>Use Variables Wisely:</strong> Fill in ALL required fields for better task clarity</li>
          <li><strong>Combine Templates:</strong> Deploy multiple complementary templates to the same list</li>
          <li><strong>Edit After Deployment:</strong> Templates are a starting point, customize as needed</li>
        </ul>

        <h3 style={{ fontSize: 18, marginTop: 20, marginBottom: 8 }}>Workflow Suggestions</h3>
        <div style={{ backgroundColor: '#fef3c7', padding: 16, borderRadius: 8, border: '1px solid #fde047' }}>
          <p style={{ margin: 0, marginBottom: 12, color: '#475569' }}>
            <strong>New Project Setup:</strong> Deploy "Project Kickoff" template, customize with your project details,
            add team members as collaborators, assign tasks and set due dates.
          </p>
          <p style={{ margin: 0, color: '#475569' }}>
            <strong>Recurring Workflows:</strong> Use same template for repeated processes (e.g., monthly reports,
            product launches) to keep deployment quick and consistent.
          </p>
        </div>
      </section>

      <div style={{ padding: 16, backgroundColor: '#eff6ff', border: '2px solid #3b82f6', borderRadius: 8 }}>
        <p style={{ margin: 0, fontSize: 16, color: '#1e40af', textAlign: 'center' }}>
          <strong>Need more templates?</strong> Visit the Marketplace to browse and purchase premium templates!
        </p>
      </div>
    </div>
  );
}

